import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useCallback
} from "react";
import PropTypes from "kf-proptypes/widgets";

import { i18n } from "base/i18n";

import {
  ICON_SIZE,
  ICON_STROKE,
  KfIcon
} from "@widgets/common/components/primitives/kf_icon/index.jsx";

import styles from "./styles.css";

export const STEPPER_PROGRESS_TYPE = { DEFAULT: "Default", ICON: "Icon" };

export const STEPPER_PROGRESS_COLOR = {
  GREEEN: "green",
  BLUE: "blue"
};

export const StepperProgress = forwardRef((props, ref) => {
  const {
    type = STEPPER_PROGRESS_TYPE.DEFAULT,
    color = STEPPER_PROGRESS_COLOR.BLUE,
    disableState,
    dataSource,
    progressState,
    updateCurrentStep
  } = props;
  const [step, setStep] = useState(0);
  const [subStep, setSubStep] = useState(0);

  const [stepDisable, setStepDisable] = useState(1);
  const [subStepDisable, setSubStepDisable] = useState(0);

  const findInitialIndex = useCallback(
    function getInitialIndex({ activeStep, activeSubStep }) {
      let index = { mainIndex: 0, subIndex: 0 };
      if (activeStep) {
        index.mainIndex = dataSource.findIndex(
          (item) => item.id === activeStep
        );
        if (activeSubStep) {
          // Find the index of activeSubStep within its parent step's subflow
          const activeStepData = dataSource[index.mainIndex];
          if (activeStepData && activeStepData.subFlow) {
            index.subIndex = activeStepData.subFlow.findIndex(
              (item) => item.id === activeSubStep
            );
          }
        }
      }
      return index;
    },
    [dataSource]
  );

  useEffect(
    function updateActiveProgressData() {
      if (progressState) {
        let progressIndex = findInitialIndex(progressState);
        setStep(progressIndex.mainIndex);
        setSubStep(progressIndex.subIndex);
      }
    },
    [findInitialIndex, progressState]
  );

  useEffect(
    function updateDisableProgressData() {
      if (disableState?.activeStep) {
        let disableIndex = findInitialIndex(disableState);
        setStepDisable(disableIndex.mainIndex + 1);
        setSubStepDisable(disableIndex.subIndex + 1);
      }
    },
    [disableState, findInitialIndex]
  );

  useImperativeHandle(ref, () => ({
    onNextClick: (props) => handleNext(props),
    onBackClick: (props) => handleBack(props)
  }));

  function handleNext({ oldStep, oldSubStep }) {
    let newStep = oldStep;
    let newSubStep = oldSubStep;

    if (
      dataSource[newStep]?.subFlow &&
      dataSource[newStep].subFlow.length > 0
    ) {
      if (oldSubStep < dataSource[newStep].subFlow.length - 1) {
        // If there are more substeps, move to the next substep
        newSubStep = newSubStep + 1;
      } else {
        // If all substeps are finished, move to the next parent step
        newStep = Math.min(newStep + 1, dataSource.length - 1);
        newSubStep = 0; // Reset substep index
      }
    } else {
      // If no subflow, go to the next step
      newStep = Math.min(newStep + 1, dataSource.length - 1);
    }

    if (stepDisable < newStep + 1) {
      setStepDisable(newStep + 1);
    }

    // Update state only if there's a change
    if (oldStep !== newStep) {
      setStep(newStep);
    }

    if (dataSource[newStep]?.subFlow && subStepDisable < newSubStep + 1) {
      setSubStepDisable(newSubStep + 1);
    }

    if (oldSubStep !== newSubStep) {
      setSubStep(newSubStep);
    }
    let currentSteps = {
      activeStep: { index: newStep, value: dataSource[newStep].id }
    };
    if (dataSource[newStep]?.subFlow) {
      currentSteps.activeSubStep = {
        index: newSubStep,
        value: dataSource[newStep].subFlow[newSubStep].id
      };
    }
    updateCurrentStep(currentSteps);
  }

  /**
   * Navigates back to the previous step in the notification flow.
   * @returns None
   */

  function handleBack({ oldStep, oldSubStep }) {
    let newStep = oldStep;
    let newSubStep = oldSubStep;

    if (newSubStep > 0) {
      // If there are previous substeps, move to the previous substep
      newSubStep = newSubStep - 1;
    } else {
      // If at the beginning of substeps, move to the previous parent step
      newStep = Math.max(newStep - 1, 0);
      if (dataSource[newStep]?.subFlow) {
        // If the previous step has a subflow, set subStep to the last substep index
        newSubStep = dataSource[newStep].subFlow.length - 1;
      } else {
        newSubStep = 0; // Reset substep index if no subflow
      }
    }
    // Update state only if there's a change
    if (oldStep !== newStep) {
      setStep(newStep);
    }

    if (oldStep !== newSubStep) {
      setSubStep(newSubStep);
    }
    let currentSteps = {
      activeStep: { index: newStep, value: dataSource[newStep].id }
    };
    if (dataSource[newStep]?.subFlow) {
      currentSteps.activeSubStep = {
        index: newSubStep,
        value: dataSource[newStep].subFlow[newSubStep].id
      };
    }
    updateCurrentStep(currentSteps);
  }

  return (
    <div className={`${styles.mainContainer} ${styles[color]}`}>
      {dataSource.map((item, index) => {
        return (
          <div
            key={item.value}
            className={`${styles.childData} ${
              index >= stepDisable ? styles.isDisabled : ""
            } ${item.subFlow ? styles.isChildFlow : ""}`}
          >
            <div
              className={`${styles.childDataMain} ${
                step === index ? styles.activeState : ""
              }`}
              onClick={(e) => {
                e.stopPropagation();
                setStep(index);
                let currentSteps = {
                  activeStep: {
                    index: index,
                    value: item.id
                  }
                };
                if (item?.subFlow) {
                  currentSteps.activeSubStep = {
                    index: 0,
                    value: item.subFlow[0].id
                  };
                }
                updateCurrentStep(currentSteps);
              }}
            >
              <div className={styles.iconWrapper}>
                {type === STEPPER_PROGRESS_TYPE.DEFAULT ? (
                  index + 1
                ) : (
                  <IconWrapper
                    icon={item.icon}
                    color={color}
                    activeIcon={step === index}
                    disabled={index >= stepDisable}
                    size={ICON_SIZE.SIZE_16}
                    storke={ICON_STROKE.REGULAR}
                  />
                )}
              </div>
              <span>{i18n._(item.value)}</span>
            </div>
            {item.subFlow && (
              <div className={styles.subData}>
                {item.subFlow.map((subItem, subIndex) => (
                  <div
                    key={subItem.value}
                    className={`${
                      index === step && subStep === subIndex
                        ? styles.activeState
                        : ""
                    } ${
                      index + 1 >= stepDisable && subIndex >= subStepDisable
                        ? styles.isDisabled
                        : ""
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSubStep(subIndex);
                      setStep(index);
                      let currentSteps = {
                        activeStep: {
                          index: index,
                          value: item.id
                        },
                        activeSubStep: {
                          index: subIndex,
                          value: subItem.id
                        }
                      };
                      updateCurrentStep(currentSteps);
                    }}
                  >
                    <div className={styles.iconWrapper}>
                      {type === STEPPER_PROGRESS_TYPE.DEFAULT ? (
                        subIndex + 1
                      ) : (
                        <IconWrapper
                          icon={subItem.icon}
                          color={color}
                          activeIcon={index === step && subStep === subIndex}
                          disabled={
                            index + 1 >= stepDisable &&
                            subIndex >= subStepDisable
                          }
                          size={ICON_SIZE.SIZE_12}
                          storke={ICON_STROKE.LIGHT}
                        />
                      )}
                    </div>
                    <span>{i18n._(subItem.value)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
});

StepperProgress.propTypes = {
  type: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  disableState: PropTypes.shape({
    activeStep: PropTypes.string,
    activeSubStep: PropTypes.string
  }),
  dataSource: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      icon: PropTypes.string,
      value: PropTypes.string.isRequired,
      subFlow: PropTypes.shape([
        {
          id: PropTypes.string,
          icon: PropTypes.string,
          value: PropTypes.string
        }
      ])
    })
  ),
  progressState: PropTypes.PropTypes.shape({
    activeStep: PropTypes.string,
    activeSubStep: PropTypes.string
  }),
  updateCurrentStep: PropTypes.func
};

function IconWrapper({ icon, color, activeIcon, disabled, size, storke }) {
  const fillColor = () => {
    if (disabled) return "--kf-color-gray-400";
    if (activeIcon) return "--kf-color-white";
    if (color === STEPPER_PROGRESS_COLOR.GREEEN)
      return "--kf-color-secondary-one-500";
    return "--kf-color-primary-500";
  };

  return <KfIcon icon={icon} size={size} stroke={storke} color={fillColor()} />;
}

IconWrapper.propTypes = {
  icon: PropTypes.string,
  color: PropTypes.string,
  activeIcon: PropTypes.bool,
  disabled: PropTypes.bool,
  size: PropTypes.string,
  storke: PropTypes.string
};
